import { type ProductDeposit } from "@kanpla/api-contract";

import { type FlexMenu } from "~/api/menus/types";
import { logger } from "~/services/logger";

import { type VariantChoice } from "./orders-create-context";

/**
 * A picked choice the catalog no longer lists charges no deposit and declares
 * none, so a stale basket has to be diagnosable from the log.
 */
export function warnOnChoicesMissingFromCatalog(
  productId: string,
  variants: FlexMenu["variants"] | undefined,
  picked: VariantChoice[]
): void {
  const known = new Set(
    (variants ?? []).flatMap((variant) =>
      variant.choices.map((choice) => choice.id)
    )
  );

  for (const choice of picked) {
    if (choice.quantity <= 0 || known.has(choice.id)) continue;

    logger.warn("Collect pre-order choice is not in the catalog", {
      product_id: productId,
      option_id: choice.variantId,
      choice_id: choice.id
    });
  }
}

export type VariantDepositSum = {
  /** Minor units, per unit of the line. */
  inclVat: number;
  exclVat: number;
  /** The catalog deposit of each picked choice that carries one, by choice id. */
  byChoiceId: Record<string, ProductDeposit>;
};

const NONE: VariantDepositSum = { inclVat: 0, exclVat: 0, byChoiceId: {} };

/**
 * The deposits of the picked variant choices, read from the catalog so the
 * till shows, guards against and declares the same fee the server resolves.
 */
export function sumVariantDeposits(
  variants: FlexMenu["variants"] | undefined,
  quantityByChoiceId: Record<string, number>
): VariantDepositSum {
  if (!variants?.length) return NONE;

  return variants
    .flatMap((variant) => variant.choices)
    .reduce<VariantDepositSum>(
      (sum, choice) => {
        const quantity = quantityByChoiceId[choice.id] ?? 0;
        if (quantity <= 0 || !choice.deposit) return sum;

        return {
          inclVat: sum.inclVat + choice.deposit.feeInclVat * quantity,
          exclVat: sum.exclVat + choice.deposit.feeExclVat * quantity,
          byChoiceId: { ...sum.byChoiceId, [choice.id]: choice.deposit }
        };
      },
      { inclVat: 0, exclVat: 0, byChoiceId: {} }
    );
}
